import { supabase } from '../lib/supabase';
export class FeedbackService {
    /**
     * Get all feedback with optional filters
     */
    static async getFeedback(options) {
        try {
            let query = supabase
                .from('feedback_submissions')
                .select(`
          *,
          feedback_categories!category_id(id, name, icon, color)
        `);
            if (options?.status && options.status !== 'all') {
                query = query.eq('status', options.status);
            }
            if (options?.category_id) {
                query = query.eq('category_id', options.category_id);
            }
            if (options?.app_id) {
                query = query.eq('app_id', options.app_id);
            }
            if (options?.search) {
                query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%`);
            }
            // Sorting
            let orderColumn = 'created_at';
            let ascending = false;
            if (options?.sortBy === 'votes') {
                orderColumn = 'vote_count';
                ascending = false;
            }
            else if (options?.sortBy === 'comments') {
                orderColumn = 'comment_count';
                ascending = false;
            }
            else {
                orderColumn = 'created_at';
                ascending = false;
            }
            query = query.order(orderColumn, { ascending });
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching feedback:', error);
                throw error;
            }
            return data || [];
        }
        catch (error) {
            console.error('FeedbackService.getFeedback error:', error);
            throw error;
        }
    }
    /**
     * Get feedback by ID
     */
    static async getFeedbackById(id) {
        try {
            const { data, error } = await supabase
                .from('feedback_submissions')
                .select(`
          *,
          feedback_categories!category_id(id, name, icon, color)
        `)
                .eq('id', id)
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('FeedbackService.getFeedbackById error:', error);
            throw error;
        }
    }
    /**
     * Submit new feedback
     */
    static async submitFeedback(feedback) {
        try {
            const { data, error } = await supabase
                .from('feedback_submissions')
                .insert([
                {
                    title: feedback.title,
                    description: feedback.description,
                    category_id: feedback.category_id,
                    app_id: feedback.app_id,
                    ecosystem_level: feedback.ecosystem_level || 'app',
                    status: 'open',
                    priority: 'medium',
                    submitted_by: feedback.submitted_by,
                    submitted_by_name: feedback.is_anonymous ? null : feedback.submitted_by_name,
                    submitted_by_avatar: feedback.submitted_by_avatar || null,
                    is_anonymous: feedback.is_anonymous,
                    vote_count: 0,
                    comment_count: 0,
                },
            ])
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('FeedbackService.submitFeedback error:', error);
            throw error;
        }
    }
    /**
     * Get user's vote on a feedback item
     */
    static async getUserVote(feedbackId, userId) {
        try {
            const { data, error } = await supabase
                .from('feedback_votes')
                .select('*')
                .eq('feedback_id', feedbackId)
                .eq('user_id', userId)
                .single();
            // If no vote found, error code PGRST116 is expected
            if (error && error.code !== 'PGRST116') {
                throw error;
            }
            return data;
        }
        catch (error) {
            // PGRST116 means no rows found, which is expected
            if (error?.code === 'PGRST116') {
                return null;
            }
            console.error('FeedbackService.getUserVote error:', error);
            throw error;
        }
    }
    /**
     * Vote on feedback (upvote/downvote)
     */
    static async voteFeedback(feedbackId, userId, voteType = 'upvote') {
        try {
            // Check if user already voted
            const existingVote = await this.getUserVote(feedbackId, userId);
            if (existingVote) {
                // Remove vote if already voted
                const { error } = await supabase
                    .from('feedback_votes')
                    .delete()
                    .eq('feedback_id', feedbackId)
                    .eq('user_id', userId);
                if (error)
                    throw error;
                // Decrement vote count
                const { data: feedback, error: fetchError } = await supabase
                    .from('feedback_submissions')
                    .select('vote_count')
                    .eq('id', feedbackId)
                    .single();
                if (!fetchError && feedback) {
                    await supabase
                        .from('feedback_submissions')
                        .update({ vote_count: Math.max(0, (feedback.vote_count || 0) - 1) })
                        .eq('id', feedbackId);
                }
                return { voted: false, action: 'removed' };
            }
            else {
                // Add vote
                const { error } = await supabase
                    .from('feedback_votes')
                    .insert([
                    {
                        feedback_id: feedbackId,
                        user_id: userId,
                        vote_type: voteType,
                    },
                ]);
                if (error)
                    throw error;
                // Increment vote count
                const { data: feedback, error: fetchError } = await supabase
                    .from('feedback_submissions')
                    .select('vote_count')
                    .eq('id', feedbackId)
                    .single();
                if (!fetchError && feedback) {
                    await supabase
                        .from('feedback_submissions')
                        .update({ vote_count: (feedback.vote_count || 0) + 1 })
                        .eq('id', feedbackId);
                }
                return { voted: true, action: 'added' };
            }
        }
        catch (error) {
            console.error('FeedbackService.voteFeedback error:', error);
            throw error;
        }
    }
    /**
     * Get all categories
     */
    static async getCategories() {
        try {
            const { data, error } = await supabase
                .from('feedback_categories')
                .select('*')
                .order('created_at', { ascending: true });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('FeedbackService.getCategories error:', error);
            throw error;
        }
    }
    /**
     * Get comments on feedback
     */
    static async getComments(feedbackId) {
        try {
            const { data, error } = await supabase
                .from('feedback_comments')
                .select('*')
                .eq('feedback_id', feedbackId)
                .order('created_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('FeedbackService.getComments error:', error);
            throw error;
        }
    }
    /**
     * Add comment to feedback
     */
    static async addComment(feedbackId, userId, content, userName, isAnonymous = false) {
        try {
            const { data, error } = await supabase
                .from('feedback_comments')
                .insert([
                {
                    feedback_id: feedbackId,
                    user_id: userId,
                    content,
                    user_name: isAnonymous ? null : userName,
                    is_anonymous: isAnonymous,
                },
            ])
                .select()
                .single();
            if (error)
                throw error;
            // Increment comment count
            const { data: feedback, error: fetchError } = await supabase
                .from('feedback_submissions')
                .select('comment_count')
                .eq('id', feedbackId)
                .single();
            if (!fetchError && feedback) {
                await supabase
                    .from('feedback_submissions')
                    .update({ comment_count: (feedback.comment_count || 0) + 1 })
                    .eq('id', feedbackId);
            }
            return data;
        }
        catch (error) {
            console.error('FeedbackService.addComment error:', error);
            throw error;
        }
    }
    /**
     * Update feedback status
     */
    static async updateFeedbackStatus(feedbackId, status) {
        try {
            const { data, error } = await supabase
                .from('feedback_submissions')
                .update({ status })
                .eq('id', feedbackId)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('FeedbackService.updateFeedbackStatus error:', error);
            throw error;
        }
    }
    /**
     * Update feedback priority
     */
    static async updateFeedbackPriority(feedbackId, priority) {
        try {
            const { data, error } = await supabase
                .from('feedback_submissions')
                .update({ priority })
                .eq('id', feedbackId)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('FeedbackService.updateFeedbackPriority error:', error);
            throw error;
        }
    }
}
